/**
 * The contract of the "Stato del reparto" feature: the diagnostic bulletin the
 * shop-floor spool sends to the cloud, and the status page the office reads.
 *
 * Every object is `.strict()` and every number and string is bounded: the
 * bulletin comes from a machine outside the cloud, so nothing arbitrary gets in.
 */
import { z } from 'zod';
const MAX = 1_000_000_000;
const zIso = z.string().datetime();
const zMotivo = z.string().max(200);
const zNum = z.number().int().min(0).max(MAX);
export const zTempi = z
    .object({ conteggio: zNum, mediaMs: zNum, p95Ms: zNum, massimoMs: zNum })
    .strict();
export const zBollettinoSpool = z
    .object({
    inviatoIl: zIso,
    spool: z
        .object({ versione: z.string().max(64), accesoDaS: zNum, inCollaudo: z.boolean() })
        .strict(),
    wms: z
        .object({
        ultimaRispostaBuona: zIso.nullable(),
        ultimoErrore: z.object({ ora: zIso, motivo: zMotivo }).strict().nullable(),
        comandi: zNum,
        buoni: zNum,
        erroriHttp: zNum,
        guasti: zNum,
        tempi: zTempi.nullable(),
        perJob: z
            .array(zTempi.extend({ job: z.string().regex(/^[A-Z0-9_-]{1,40}$/) }).strict())
            .max(30),
        prova: z
            .object({
            ora: zIso,
            ok: z.boolean(),
            durataMs: zNum.nullable(),
            motivo: zMotivo.nullable(),
        })
            .strict()
            .nullable(),
    })
        .strict(),
    cloud: z
        .object({
        chiamate: zNum,
        fallite: zNum,
        ultimoMotivo: zMotivo.nullable(),
        // WMS replies the cloud never got. Optional: a spool from before does
        // not send it, and the cloud is updated first.
        esitiPersi: zNum.optional(),
    })
        .strict(),
    etichette: z
        .object({
        configurata: z.boolean(),
        scrivibile: z.boolean().nullable(),
        liberoMb: zNum.nullable(),
        ultimaScritta: zIso.nullable(),
    })
        .strict(),
    bilancia: z
        .object({
        accesa: z.boolean(),
        motivo: zMotivo.nullable(),
        ultimaMisura: zIso.nullable(),
        inCoda: zNum,
        piuVecchiaIl: zIso.nullable(),
        scartate: zNum,
        ultimoGiro: z.enum(['vuoto', 'inviate', 'fermo']).nullable(),
        liberoMb: zNum.nullable(),
    })
        .strict(),
    errori: z
        .array(z.object({ ora: zIso, job: z.string().max(40), durataMs: zNum, motivo: zMotivo }).strict())
        .max(5),
})
    .strict();
export const zColore = z.enum(['verde', 'giallo', 'rosso', 'grigio']);
export const zPezzo = z.enum([
    'spool',
    'rete',
    'wms',
    'etichette',
    'bilancia',
    'database',
    'catalogo',
]);
const zSemaforo = z.object({ colore: zColore, perche: z.string().max(200) }).strict();
export const zStatoReparto = z
    .object({
    cloud: z
        .object({
        versione: z.string().nullable(),
        database: z.enum(['postgres', 'memoria', 'irraggiungibile']),
        catalogo: z.enum(['deconetwork', 'prova']),
        magazzino: z.enum(['modula', 'spento']),
        spool: z
            .object({
            stato: z.enum(['collegato', 'assente', 'non usato']),
            ultimoContatto: zIso.nullable(),
            versione: z.string().nullable(),
        })
            .strict(),
    })
        .strict(),
    bollettino: zBollettinoSpool.nullable(),
    ricevutoIl: zIso.nullable(),
    sfasamentoS: z.number().int().nullable(),
    semafori: z
        .object(Object.fromEntries(zPezzo.options.map((p) => [p, zSemaforo])))
        .strict(),
})
    .strict();
//# sourceMappingURL=shop-floor-status.js.map